import { languageCodeToName, toIetfLanguageTag } from './language';

function matchAnyCode(code: string, words: { [code: string]: string }) {
  const languageCode = toIetfLanguageTag(code);
  const components = languageCode.split('-');

  for (let end = components.length; end > 0; end -= 1) {
    const codeToTest = components.slice(0, end).join('-');
    if (words[codeToTest]) return words[codeToTest];
  }
  return null;
}

/**
 * Based on the ISO 639-1
 * Intentionally not using `_t`, since we want to keep the language names in their own language
 * Returns the English (translated to the user's language) if not found.
 *
 * The data source for language local names is https://wikistats.wmcloud.org/display.php?t=wp,
 * which is used by Wikipedia, unless with other source in comment.
 */
function languageTagToLocalName(code: string): string {
  const names = {
    aa: 'Afar',
    ab: 'Аҧсуа',
    // https://en.wikipedia.org/wiki/Avestan
    ae: '𐬎𐬞𐬀𐬯𐬙𐬀𐬎𐬎𐬀𐬐𐬀𐬉𐬥𐬀',
    af: 'Afrikaans',
    ak: 'Akana',
    am: 'አማርኛ',
    an: 'Aragonés',
    ar: 'العربية',
    'ar-EG': 'مصرى (Maṣrī)',
    as: 'অসমীয়া',
    av: 'Авар',
    ay: 'Aymar',
    az: 'Azərbaycanca',
    ba: 'Башҡорт',
    be: 'Беларуская',
    bg: 'Български',
    bh: 'भोजपुरी',
    bi: 'Bislama',
    bm: 'Bamanankan',
    bn: 'বাংলা',
    bo: 'བོད་སྐད',
    br: 'Brezhoneg',
    bs: 'Bosanski',
    ca: 'Català',
    ce: 'Нохчийн',
    ch: 'Chamoru',
    co: 'Corsu',
    cr: 'Nehiyaw',
    cs: 'Čeština',
    cu: 'Словѣньскъ',
    cv: 'Чăваш',
    cy: 'Cymraeg',
    da: 'Dansk',
    de: 'Deutsch',
    dv: 'ދިވެހިބަސް',
    dz: 'ཇོང་ཁ',
    ee: 'Eʋegbe',
    el: 'Ελληνικά',
    en: 'English',
    eo: 'Esperanto',
    es: 'Español',
    et: 'Eesti',
    eu: 'Euskara',
    fa: 'فارسی',
    // https://en.wikipedia.org/wiki/Dari
    'fa-AF': 'دری',
    ff: 'Fulfulde',
    fi: 'Suomi',
    fj: 'Na Vosa Vakaviti',
    fo: 'Føroyskt',
    fr: 'Français',
    fy: 'Frysk',
    ga: 'Gaeilge',
    gd: 'Gàidhlig',
    gl: 'Galego',
    gn: "Avañe'ẽ",
    gu: 'ગુજરાતી',
    gv: 'Gaelg',
    ha: 'هَوُسَ',
    he: 'עברית',
    hi: 'हिन्दी',
    ho: 'Hiri Motu',
    hr: 'Hrvatski',
    ht: 'Krèyol ayisyen',
    hu: 'Magyar',
    hy: 'Հայերեն',
    hz: 'Otsiherero',
    ia: 'Interlingua',
    id: 'Bahasa Indonesia',
    ig: 'Igbo',
    ii: 'ꆇꉙ',
    ik: 'Iñupiak',
    io: 'Ido',
    is: 'Íslenska',
    it: 'Italiano',
    iu: 'ᐃᓄᒃᑎᑐᑦ',
    ja: '日本語',
    // https://en.wikipedia.org/wiki/Javanese_language
    jv: 'Basa Jawa / ꦧꦱꦗꦮ',
    ka: 'ქართული',
    kg: 'KiKongo',
    ki: 'Gĩkũyũ',
    kj: 'Kuanyama',
    kk: 'Қазақша',
    kl: 'Kalaallisut',
    km: 'ភាសាខ្មែរ',
    kn: 'ಕನ್ನಡ',
    ko: '한국어',
    kr: 'Kanuri',
    ks: 'कश्मीरी / كشميري',
    ku: 'Kurdî / كوردی',
    kv: 'Коми',
    kw: 'Kernewek / Karnuack',
    ky: 'Кыргызча',
    la: 'Latina',
    lb: 'Lëtzebuergesch',
    lg: 'Luganda',
    li: 'Limburgs',
    ln: 'Lingala',
    lo: 'ລາວ',
    lt: 'Lietuvių',
    // https://en.wikipedia.org/wiki/Luba-Katanga_language
    lu: 'Tshilubà',
    lv: 'Latviešu',
    mg: 'Malagasy',
    mh: 'Ebon',
    mi: 'Māori',
    mk: 'Македонски',
    ml: 'മലയാളം',
    mn: 'Монгол',
    mr: 'मराठी',
    ms: 'Bahasa Melayu',
    mt: 'Malti',
    my: 'မြန်မာဘာသာ',
    na: 'Dorerin Naoero',
    // https://en.wikipedia.org/wiki/Bokm%C3%A5l
    nb: 'Norsk (Bokmål)',
    // https://en.wikipedia.org/wiki/Northern_Ndebele_language
    nd: 'North Ndebele',
    ne: 'नेपाली',
    ng: 'Oshiwambo',
    nl: 'Nederlands',
    // https://en.wikipedia.org/wiki/Nynorsk
    nn: 'Norsk (Nynorsk)',
    // https://en.wikipedia.org/wiki/Norwegian_language
    no: 'Norsk',
    // https://en.wikipedia.org/wiki/Southern_Ndebele_language
    nr: 'South Ndebele',
    nv: 'Diné bizaad',
    ny: 'Chi-Chewa',
    oc: 'Occitan',
    // https://en.wikipedia.org/wiki/Ojibwe_language
    oj: 'Anishinaabemowin / ᐊᓂᐦᔑᓈᐯᒧᐎᓐ',
    om: 'Oromoo',
    or: 'ଓଡ଼ିଆ',
    os: 'Иронау',
    pa: 'ਪੰਜਾਬੀ',
    pi: 'पाऴि',
    pl: 'Polski',
    ps: 'پښتو',
    pt: 'Português',
    // chrome://settings/languages
    'pt-BR': 'Português (Brasil)',
    // chrome://settings/languages
    'pt-PT': 'Português (Portugal)',
    qu: 'Runa Simi',
    rm: 'Rumantsch',
    rn: 'Kirundi',
    ro: 'Română',
    ru: 'Русский',
    rw: 'Ikinyarwanda',
    sa: 'संस्कृतम्',
    sc: 'Sardu',
    sd: 'سنڌي، سندھی ، सिन्ध',
    se: 'Sámegiella',
    sg: 'Sängö',
    si: 'සිංහල',
    sk: 'Slovenčina',
    sl: 'Slovenščina',
    sm: 'Gagana Samoa',
    sn: 'chiShona',
    so: 'Soomaaliga',
    sq: 'Shqip',
    sr: 'Српски / Srpski',
    ss: 'SiSwati',
    st: 'Sesotho',
    su: 'Basa Sunda',
    sv: 'Svenska',
    sw: 'Kiswahili',
    ta: 'தமிழ்',
    te: 'తెలుగు',
    tg: 'Тоҷикӣ',
    th: 'ไทย',
    ti: 'ትግርኛ',
    tk: 'تركمن / Туркмен',
    tl: 'Tagalog',
    tn: 'Setswana',
    to: 'faka Tonga',
    tr: 'Türkçe',
    ts: 'Xitsonga',
    tt: 'Tatarça / Татарча',
    tw: 'Twi',
    ty: 'Reo Mā`ohi',
    ug: 'ئۇيغۇر تىلى',
    uk: 'Українська',
    ur: 'اردو',
    uz: 'O‘zbek',
    ve: 'Tshivenḓa',
    vi: 'Tiếng Việt',
    vo: 'Volapük',
    wa: 'Walon',
    wo: 'Wolof',
    xh: 'isiXhosa',
    yi: 'ייִדיש',
    yo: 'Yorùbá',
    za: 'Cuengh',
    zh: '中文',
    // chrome://settings/languages
    'zh-CN': '中文（简体）',
    // chrome://settings/languages
    'zh-TW': '中文（繁體）',
    zu: 'isiZulu',
  };

  const nativeLanguage = matchAnyCode(code, names);
  if (nativeLanguage) return nativeLanguage;
  return languageCodeToName(code);
}

// Created with Chat-GPT, since i don't speak these languages
function getLabelForAutotranslation(code: string): string {
  const translations: { [key: string]: string } = {
    de: 'Auto',
    ru: 'авто',
    zh: '自动',
    ja: 'オート',
    ko: '자동',
    ar: 'آلي',
    fr: 'aut.',
    hi: 'ऑटो',
    he: 'אוטומטי',
    tr: 'otomatik',
    hu: 'automata',
    el: 'αυτόματο',
    id: 'otomatis',
    th: 'อัตโนมัติ',
    vi: 'tự động',
    tl: 'awto',
    af: 'outomaties',
    sq: 'automatik',
    uk: 'авто',
    bg: 'авто',
    et: 'automaatne',
    fa: 'خودکار',
    hr: 'automatski',
    hy: 'ավտոմատ',
    ka: 'ავტომატური',
    lt: 'automatinis',
    lv: 'automātisks',
    mk: 'автоматско',
    mr: 'ऑटो',
    pl: 'automatyczny',
    ro: 'automat',
    sl: 'avtomatsko',
    sr: 'аутоматски',
    uz: 'avtomatik',
    az: 'avto',
    bn: 'স্বয়ংক্রিয়',
    da: 'auto',
    'fa-AF': 'خودکار',
    ml: 'ഓട്ടോ',
    ps: 'خودکار',
    'pt-PT': 'automático',
    ta: 'ஆட்டோ',
    ur: 'خودکار',
  };
  return matchAnyCode(code, translations) || 'auto';
}

export { languageTagToLocalName, getLabelForAutotranslation };
